'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useRef, useState, type ReactNode } from 'react';

interface CarouselProps {
  slides: ReactNode[];
  slidesPerView?: number;
  prevLabel?: string;
  nextLabel?: string;
  dotLabels?: string[];
  viewportClassName?: string;
  className?: string;
  slideClassName?: string;
  /**
   * Horizontal alignment of the slide row. Only visible on a partial page —
   * one that holds fewer slides than `slidesPerView` — where `'start'` leaves
   * the unused width on the right instead of splitting it either side.
   * Defaults to `'center'`, which is what every other consumer expects.
   */
  slideAlignment?: 'center' | 'start';
  showDots?: boolean;
  showArrows?: boolean;
  arrowsOnBottom?: boolean;
  captions?: ReactNode[];
  onPageChange?: (pageIndex: number) => void;
  /** Fires on prev navigation specifically (arrow click or swipe-right), before onPageChange. */
  onPrev?: () => void;
  /** Fires on next navigation specifically (arrow click or swipe-left), before onPageChange. */
  onNext?: () => void;
}

const SWIPE_THRESHOLD = 30;

export const Carousel = ({
  slides,
  slidesPerView = 1,
  prevLabel = 'Previous',
  nextLabel = 'Next',
  dotLabels,
  viewportClassName,
  className,
  showDots,
  showArrows = true,
  arrowsOnBottom = false,
  slideClassName = '',
  slideAlignment = 'center',
  captions,
  onPageChange,
  onPrev,
  onNext,
}: CarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<'right' | 'left'>('right');
  const touchStartX = useRef<number | null>(null);
  const swiped = useRef(false);

  const total = slides.length;

  if (total === 0) {
    return null;
  }

  const pageCount = Math.ceil(total / slidesPerView);
  const maxIndex = pageCount - 1;
  const safeIndex = Math.min(activeIndex, maxIndex);
  const slideWidthPercent = 100 / slidesPerView;
  const shouldShowDots = showDots ?? slidesPerView === 1;

  const navigate = (dir: 'right' | 'left', newIndex: number) => {
    setDirection(dir);
    setActiveIndex(newIndex);
    onPageChange?.(newIndex);
  };
  const goPrev = () => {
    onPrev?.();
    navigate('left', activeIndex <= 0 ? maxIndex : activeIndex - 1);
  };
  const goNext = () => {
    onNext?.();
    navigate('right', activeIndex >= maxIndex ? 0 : activeIndex + 1);
  };
  const goTo = (i: number) => navigate(i > activeIndex ? 'right' : 'left', i);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    swiped.current = false;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null || swiped.current) {
      return;
    }
    const delta = e.touches[0].clientX - touchStartX.current;
    if (Math.abs(delta) < SWIPE_THRESHOLD) {
      return;
    }
    swiped.current = true;
    if (delta < 0) {
      goNext();
    } else {
      goPrev();
    }
  };
  const onTouchEnd = () => {
    touchStartX.current = null;
    swiped.current = false;
  };

  const arrowBtn = (dir: 'prev' | 'next') => {
    const isNext = dir === 'next';
    return (
      <button
        type="button"
        onClick={isNext ? goNext : goPrev}
        aria-label={isNext ? nextLabel : prevLabel}
        className="text-primary min-h-11 min-w-11 shrink-0 p-3 transition-opacity hover:opacity-70"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d={isNext ? 'M10 6l6 6-6 6' : 'M14 6l-6 6 6 6'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    );
  };

  const dots = () =>
    shouldShowDots ? (
      <div className="my-4 flex items-center justify-center gap-3">
        {Array.from({ length: pageCount }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={dotLabels?.[i] ?? `Go to slide ${i + 1}`}
            aria-current={i === safeIndex}
            className={classNames(
              'h-2.5 w-2.5 rounded-full transition-colors',
              i === safeIndex ? 'bg-primary' : 'bg-dot-inactive',
            )}
          />
        ))}
      </div>
    ) : null;

  const animClass =
    direction === 'right' ? 'carousel-enter-right' : 'carousel-enter-left';

  /*
   * Every page is rendered, and all but the shown one carry the `hidden`
   * attribute (Tailwind's preflight makes that `display: none`). A search
   * crawler reads the server HTML and never clicks an arrow, so a carousel
   * that rendered only the current page put the rest of its slides out of
   * reach of indexing — the tutors behind the first three, the fifth video,
   * every review after the first (SH-564).
   *
   * Nothing about this is visible: the viewport still shows one page, and a
   * hidden page is out of the accessibility tree and off the tab order, so
   * there is no second copy for a screen reader or a keyboard to wade through.
   * Images on a hidden page stay lazy and unfetched — the document grows, the
   * page load does not.
   */
  const viewport = (
    <div
      className={classNames('grow overflow-hidden', viewportClassName)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {Array.from({ length: pageCount }, (_, page) => {
        const isActive = page === safeIndex;
        const pageStart = page * slidesPerView;
        return (
          <div key={page} data-carousel-page={page} hidden={!isActive}>
            {/*
              Keyed on whether this is the shown page, so stepping onto a page
              remounts its row: the slide-in animation replays and the slides
              start from a clean state. That is what `key={safeIndex}` on the
              single rendered row did before, and consumers depend on it — the
              video widget clears its opened videos on every page change.
            */}
            <div
              key={isActive ? 'shown' : 'idle'}
              className={classNames(
                'flex',
                slideAlignment === 'start' ? 'justify-start' : 'justify-center',
                isActive && animClass,
              )}
            >
              {slides
                .slice(pageStart, pageStart + slidesPerView)
                .map((slide, i) => (
                  <div
                    key={pageStart + i}
                    className={slideClassName}
                    style={{ width: `${slideWidthPercent}%` }}
                    aria-label={`Slide ${pageStart + i + 1} of ${total}`}
                    aria-roledescription="slide"
                    role="group"
                  >
                    {slide}
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={className} role="region" aria-roledescription="carousel">
      {arrowsOnBottom ? (
        <>
          {viewport}
          {/* The caption belongs to the slide, not to the controls: a carousel
              with a single page draws no arrows and no dots (SH-445) but its
              caption must still render. Only the arrows stay conditional. */}
          {(captions || showArrows) && (
            <div className="mt-4 flex w-full items-center">
              {showArrows && arrowBtn('prev')}
              <div className="flex-1 text-center text-sm">
                {captions?.[safeIndex]}
              </div>
              {showArrows && arrowBtn('next')}
            </div>
          )}
          {shouldShowDots && <div className="mt-2">{dots()}</div>}
        </>
      ) : (
        <>
          <div className="flex items-center gap-1">
            {showArrows && arrowBtn('prev')}
            {viewport}
            {showArrows && arrowBtn('next')}
          </div>
          {shouldShowDots && <div className="mt-4">{dots()}</div>}
        </>
      )}
    </div>
  );
};
